import type { Product } from "../data/productTypes";
import { getPurchasableProducts } from "../components/ProductPicker/ProductPicker";

type EnrolmentLike = { className: string; isActive?: boolean; isTrial?: boolean };

export function findProductForClassName(
  products: readonly Product[],
  className: string,
): Product | undefined {
  return products.find(
    (p) =>
      p.title === className ||
      p.subjectNames.includes(className) ||
      (p.legacyClassNames ?? []).includes(className),
  );
}

/** Products covered by an active paid enrolment. Trials and expired enrolments can still be bought. */
export function getOwnedProductIds(
  products: readonly Product[],
  enrolments: readonly EnrolmentLike[],
): Set<string> {
  const owned = new Set<string>();
  enrolments
    .filter((e) => e.isActive !== false && !e.isTrial)
    .forEach((e) => {
      const product = findProductForClassName(products, e.className);
      if (product) {
        owned.add(product.id);
      }
    });
  return owned;
}

/** Products whose free trial has ended. Paying converts the trial row, so these are never also owned. */
export function getTrialledProductIds(
  products: readonly Product[],
  enrolments: readonly EnrolmentLike[],
): Set<string> {
  const trialled = new Set<string>();
  enrolments
    .filter((e) => e.isTrial && e.isActive === false)
    .forEach((e) => {
      const product = findProductForClassName(products, e.className);
      if (product) {
        trialled.add(product.id);
      }
    });
  return trialled;
}

/** Products the user paid for whose access has lapsed, unless another enrolment still covers them. */
export function getRenewableProductIds(
  products: readonly Product[],
  enrolments: readonly EnrolmentLike[],
): Set<string> {
  const owned = getOwnedProductIds(products, enrolments);
  const renewable = new Set<string>();
  enrolments
    .filter((e) => !e.isTrial && e.isActive === false)
    .forEach((e) => {
      const product = findProductForClassName(products, e.className);
      if (product && !owned.has(product.id)) {
        renewable.add(product.id);
      }
    });
  return renewable;
}

/**
 * Splits trial labels into banks still being trialled and banks whose trial has ended.
 * `trial` comes from the session and is updated live during practice, so it overrides the enrolment list,
 * which only learns a trial has ended when it is fetched again.
 */
export function getTrialProductIds(
  products: readonly Product[],
  enrolments: readonly EnrolmentLike[],
  trial: { className: string; ended: boolean } | null,
): { trialling: Set<string>; trialled: Set<string> } {
  const trialling = new Set<string>();
  const trialled = getTrialledProductIds(products, enrolments);
  const trialProduct = trial ? findProductForClassName(products, trial.className) : undefined;
  if (trial && trialProduct && !getOwnedProductIds(products, enrolments).has(trialProduct.id)) {
    if (trial.ended) {
      trialled.add(trialProduct.id);
    } else {
      trialled.delete(trialProduct.id);
      trialling.add(trialProduct.id);
    }
  }
  return { trialling, trialled };
}

/** Moves the bank being (or that was) trialled to the front, so it leads the list when the user is asked to buy. */
export function withTrialProductFirst(
  products: readonly Product[],
  trial: { className: string } | null,
): Product[] {
  const trialProduct = trial ? findProductForClassName(products, trial.className) : undefined;
  if (!trialProduct) {
    return [...products];
  }
  return [trialProduct, ...products.filter((p) => p.id !== trialProduct.id)];
}

/** Whether there is any bank left that the user could buy. */
export function hasProductsToAdd(products: readonly Product[], enrolments: readonly EnrolmentLike[]): boolean {
  const owned = getOwnedProductIds(products, enrolments);
  return getPurchasableProducts(products).some((p) => !owned.has(p.id));
}
